/**
 * Spec 76 Phase A — Panel type registry for the ContextPanel framework.
 *
 * Ships empty. Consumer specs (Spec 77 onwards) call `registerPanelType`
 * at module initialisation to plug in their renderers.
 *
 * See §4.1 of the spec for the full design contract.
 */
import type { ReactNode } from 'react';
import type { ConversationEntryPoint } from '../../hooks/useAssistantConversation.js';

// ─── Public interface ─────────────────────────────────────────────────────────

export interface PanelTypeDefinition {
  /**
   * Unique string identifier. Examples: 'workflow', 'tool', 'artifact'.
   * Used as the `<type>` segment of `?panel=<type>:<id>`.
   */
  type: string;

  /**
   * Panel mode.
   *
   * - `'editor'`: The panel has its own assistant thread. When open, the
   *   parent conversation's chat rail swaps to the entity's assistant
   *   thread. A breadcrumb renders above the thread.
   *
   * - `'viewer'`: Read-only / no assistant thread. The parent chat rail
   *   remains unchanged while the panel is open.
   */
  mode: 'editor' | 'viewer';

  /**
   * For `'editor'` types only — where in the app the entity's assistant
   * thread is opened from. The server names the persona for it. Mirrors
   * the prop accepted by `EntityAssistantDrawer`.
   */
  entryPoint?: ConversationEntryPoint;

  /**
   * Renders the right-side panel body. Receives the entity id parsed from
   * the URL and an `onClose` callback that removes `?panel=` from the URL.
   */
  render: (props: { id: string; onClose: () => void }) => ReactNode;

  /**
   * The record's kind as the person reads it ("Strategy", "Backtest"): the panel's heading and the group a
   * conversation's records are listed under. A type named after a card's `entity` opens that card's record inside the
   * conversation (owner 2026-10-31); without a label the type is not one of a conversation's records.
   */
  label?: string;

  /** The record's own screen in the detailed view, for "Open in detailed view". */
  detailHref?: (id: string) => string;

  /** The named policy the record's screen requires; a person without it is not offered the panel. */
  policy?: string;

  /**
   * For `'editor'` types only — produces the close-summary text that is
   * posted as a system message to the parent conversation when the panel
   * closes. Return `null` to skip the echo card.
   */
  generateCloseSummary?: (id: string) => Promise<string | null>;
}

// ─── Module-level registry ────────────────────────────────────────────────────

const _registry = new Map<string, PanelTypeDefinition>();

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Register a panel type definition.
 *
 * @throws {Error} If a definition with the same `type` has already been
 *   registered. This guards against accidental double-registration during
 *   module hot-reload in development — each consumer should call this
 *   exactly once at module scope.
 */
export function registerPanelType(def: PanelTypeDefinition): void {
  if (_registry.has(def.type)) {
    throw new Error(
      `[ContextPanel] Panel type "${def.type}" is already registered. ` +
        'Call registerPanelType exactly once per type.',
    );
  }
  _registry.set(def.type, def);
}

/**
 * Look up a registered panel type by its string identifier.
 * Returns `null` if not found (i.e. the URL contains an unrecognised type).
 */
export function getPanelType(type: string): PanelTypeDefinition | null {
  return _registry.get(type) ?? null;
}

/**
 * Clear the registry.
 *
 * **For test use only.** Call in `afterEach` to reset between test cases
 * that exercise `registerPanelType`. Do not call in production code.
 */
export function clearPanelTypeRegistry(): void {
  _registry.clear();
}
